// src/utils/materialSummary.js
// P4-3 (30 Oct 2026, D-319) - the Material Summary rows, one row per Item Master item.
//
// WHY THIS FILE EXISTS
// MaterialSummary.js had its own last-wins lookup keyed by id, doc id, code and
// name. With 451 of 646 items sharing a code, a stock doc or MRF line could land
// on the wrong item, and it read `qty || current_stock`, so a real stock of 0
// fell through to the junk `current_stock` (MED-22). Now:
//   - every line goes through matchItem(): never merged into another item
//   - stock = `qty` from the real doc only; 0 stays 0; junk docs are not read,
//     only counted. Which doc: stockDocs.js (P4-6), the one rule for every page
//   - what cannot be placed is listed under Needs attention with the reason,
//     never dropped
//   - tool lines carry an asset doc id as item_id, so they never reach
//     matchItem(); they have their own Tools list
// Pure (no Firebase) -> covered by src/utils/materialSummary.test.js (Jest).

import { itemNameKey, matchItem } from "./itemMatch";
import {
  TOOL_CATEGORIES as EXCLUDED_CATEGORIES,
  groupStockDocs,
  hasToolCode,
  pickStockDoc,
  tooManyDocsReason,
  whyNotMatched,
} from "./stockDocs";

const COUNTED_MRF_STATUSES = ["approved", "pending", "partially_issued", "issued"];

const clean = (v) => String(v ?? "").trim();
const upper = (v) => clean(v).toUpperCase();
const categoryOf = (item) => upper(item?.category || item?.item_category);

const isToolLine = (mrf, line) =>
  line.is_tool === true || mrf.request_type === "tool" ||
  EXCLUDED_CATEGORIES.includes(upper(line.category)) || hasToolCode(line.item_code);

/**
 * @param {Object} input
 * @param {Array}  input.items      Item Master docs ({ id, ... }), own + shared "", inactive included
 * @param {Array}  input.stockDocs  inventory_summary docs as { id, data }
 * @param {Array}  input.mrfs       material_requests data
 * @param {string} input.companyId
 * @returns {{ rows: Array, attention: Array, tools: Array, ignoredOldDocs: number }}
 */
export function buildMaterialSummary({ items = [], stockDocs = [], mrfs = [], companyId } = {}) {
  const master = items.filter((it) => it && (it.company_id === companyId || it.company_id === ""));
  const rows = {};
  const attentionMap = new Map();
  const toolsMap = new Map();

  const addAttention = ({ reason, item_id = "", item_code = "", item_name = "", unit = "", qty = 0, source }) => {
    const key = `${reason}|${clean(item_id)}|${itemNameKey(item_name)}`;
    const entry = attentionMap.get(key) || { reason, item_id: clean(item_id), item_code, item_name, unit, qty: 0, lines: 0, source };
    entry.qty += Number(qty || 0);
    entry.lines += 1;
    attentionMap.set(key, entry);
  };

  // ── 1. Stock: real docs only, grouped per item ────────────────
  const { byItem, unplaced, junkDocs } = groupStockDocs(master, stockDocs);
  for (const doc of unplaced) addAttention({ ...doc, source: "Stock" });

  for (const [canonicalId, { item, docs }] of byItem) {
    if (item.is_active === false) continue;

    const chosen = pickStockDoc(docs, canonicalId);
    const inv = (chosen || docs[0]).data;
    const category = categoryOf(item) || upper(inv.category);
    if (EXCLUDED_CATEGORIES.includes(category)) continue;
    if (!category && hasToolCode(inv.item_code)) continue;

    if (!chosen) {
      addAttention({ reason: tooManyDocsReason(docs.length), item_id: canonicalId, item_code: item.item_code, item_name: item.item_name, unit: inv.unit, source: "Stock" });
      continue;
    }
    rows[canonicalId] = {
      item_id:        canonicalId,
      item_code:      inv.item_code || item.item_code || "",
      item_name:      inv.item_name || item.item_name || "",
      category,
      unit:           inv.unit || item.unit || "nos",
      in_stock:       inv.qty,
      defective:      Number(inv.defective_stock || 0),
      scrap:          Number(inv.scrap_stock     || 0),
      minimum_stock:  Number(inv.minimum_stock   || 0),
      pending_mrfs:   0,
      approved_total: 0,
    };
  }

  // ── 2. MRF lines: pending + approved ──────────────────────────
  const addTool = (line) => {
    const key = clean(line.item_id) || itemNameKey(line.item_name);
    const entry = toolsMap.get(key) || { item_name: clean(line.item_name), item_code: clean(line.item_code), unit: line.unit || "nos", qty: 0, lines: 0 };
    entry.qty += Number(line.approved_qty || line.qty || 0);
    entry.lines += 1;
    toolsMap.set(key, entry);
  };

  for (const mrf of mrfs) {
    if (!COUNTED_MRF_STATUSES.includes(mrf?.status)) continue;

    for (const line of mrf.reviewed_items || mrf.items || []) {
      if (isToolLine(mrf, line)) { addTool(line); continue; }

      const itemId = clean(line.item_id);
      const lineQty = line.approved_qty || line.qty || 0;
      const shown = { item_id: itemId, item_code: line.item_code, item_name: line.item_name, unit: line.unit || line.uom, qty: lineQty, source: "MRF" };
      if (!itemId || line.is_custom_item) {
        addAttention({ ...shown, reason: "Custom item — not linked to Item Master" });
        continue;
      }

      const r = matchItem(master, { item_id: itemId, item_code: line.item_code, item_name: line.item_name });
      if (!r.item) { addAttention({ ...shown, reason: whyNotMatched(r) }); continue; }
      if (r.item.is_active === false) continue;
      if (EXCLUDED_CATEGORIES.includes(categoryOf(r.item))) { addTool(line); continue; }

      const canonicalId = clean(r.item.item_id) || itemId;
      if (!rows[canonicalId]) {
        rows[canonicalId] = {
          item_id:        canonicalId,
          item_code:      r.item.item_code || line.item_code || "",
          item_name:      r.item.item_name || line.item_name || "",
          category:       categoryOf(r.item) || upper(line.category),
          unit:           r.item.unit || line.unit || line.uom || "nos",
          in_stock:       0,
          defective:      0,
          scrap:          0,
          minimum_stock:  0,
          pending_mrfs:   0,
          approved_total: 0,
        };
      }

      const approvedQty  = Number(line.approved_qty || line.qty || 0);
      const issuedQty    = Number(line.issued_qty   || 0);
      const requestedQty = Number(line.qty          || 0);
      const pendingQty   = Math.max(approvedQty - issuedQty, 0);

      if (["approved", "partially_issued", "issued"].includes(mrf.status)) {
        rows[canonicalId].approved_total += issuedQty || approvedQty;
      }
      if (mrf.status === "pending") rows[canonicalId].pending_mrfs += requestedQty;
      if (mrf.status === "partially_issued" && pendingQty > 0) rows[canonicalId].pending_mrfs += pendingQty;
    }
  }

  return {
    rows:      Object.values(rows).sort((a, b) => a.item_name.localeCompare(b.item_name)),
    attention: [...attentionMap.values()].sort((a, b) => a.item_name.localeCompare(b.item_name)),
    tools:     [...toolsMap.values()].sort((a, b) => a.item_name.localeCompare(b.item_name)),
    ignoredOldDocs: junkDocs,
  };
}
